import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { OrganisationContact } from "../../organisation/objects/organisationContact";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_NETWORK, EMPTY_NETWORK_LIST_CONFIGURATION, EMPTY_NETWORK_LIST_CRITERIA, EMPTY_NETWORK_MARGIN_LIST_CONFIGURATION, EMPTY_NETWORK_MARGIN_LIST_CRITERIA, NETWORK_MARGIN_MODULE, NETWORK_MODULE } from "../constant/networkConstant";
import { Network } from "../objects/network";
import { NetworkListCriteria } from "../objects/networkListCriteria";
import { NetworkState } from "../objects/networkstate";
import { setNetworkPreference } from "../utils/setNetworkPreference";
import { Margin } from "../../margin/objects/margin";
import { setMarginPreference } from "../utils/setMarginPreference";
import { MarginListCriteria } from "../objects/marginListCriteria";
import { Column } from "../objects/column";


const initialState: NetworkState = {
    network_list: [],
    network_list_configuration: EMPTY_NETWORK_LIST_CONFIGURATION,
    network_list_loading: false,
    network_list_count: 0,
    network_list_criteria: EMPTY_NETWORK_LIST_CRITERIA,
    network: EMPTY_NETWORK,
    network_contacts: [],
    network_contacts_loading: false,
    network_contact: null,
    network_sub_networks: [],
    network_sub_networks_loading: false,
    network_sub_network: null,
    margins: [],
    margins_configuration: EMPTY_NETWORK_MARGIN_LIST_CONFIGURATION,
    margin: null,
    margins_loading: false,
    margins_count: 0,
    margins_criteria: EMPTY_NETWORK_MARGIN_LIST_CRITERIA,
    suspended_margin: null,
    first_preference_loaded: false
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setSuspendedMarginReducer = (state: NetworkState, action: PayloadAction<Margin | null>): NetworkState => ({
    ...state, suspended_margin: action.payload
});

const addMarginsConfigurationPreferenceReducer = (state: NetworkState, action: PayloadAction<Preferences[]>): NetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: [...state.margins_configuration.preferences, ...action.payload] }
});

const setMarginsConfigurationPreferenceReducer = (state: NetworkState, action: PayloadAction<Preferences[]>): NetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: action.payload }
});

const toggleColumnMarginsReducer = (state: NetworkState, action: PayloadAction<number>): NetworkState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const setNetworkMarginsReducer = (state: NetworkState, action: PayloadAction<Margin[]>): NetworkState => ({
    ...state, margins: action.payload
});
const updateMarginsCriteriaReducer = (state: NetworkState, action: PayloadAction<MarginListCriteria>): NetworkState => ({
    ...state, margins_criteria: action.payload
});
const updateMarginReducer = (state: NetworkState, action: PayloadAction<Margin | null>): NetworkState => ({
    ...state, margin: action.payload
});
const updateMarginsLoadingReducer = (state: NetworkState, action: PayloadAction<boolean>): NetworkState => ({
    ...state, margins_loading: action.payload
});
const updateMarginsCountReducer = (state: NetworkState, action: PayloadAction<number>): NetworkState => ({
    ...state, margins_count: action.payload
});

const addNetworkContactReducer = (state: NetworkState, action: PayloadAction<OrganisationContact>): NetworkState => ({
    ...state, network_contacts: [action.payload, ...state.network_contacts]
});

const updateNetworkContactReducer = (state: NetworkState, action: PayloadAction<OrganisationContact>): NetworkState => ({
    ...state, network_contacts: state.network_contacts.map((contact) => contact.id === action.payload.id ? action.payload : contact)
});

const updateNetworkContactsReducer = (state: NetworkState, action: PayloadAction<OrganisationContact[]>): NetworkState => ({
    ...state, network_contacts: action.payload
});

const deleteNetworkContactReducer = (state: NetworkState, action: PayloadAction<number>): NetworkState => ({
    ...state, network_contacts: state.network_contacts.filter((contact) => contact.id !== action.payload)
});

const setNetworkSubNetworkReducer = (state: NetworkState, action: PayloadAction<null | Network>): NetworkState => ({
    ...state, network_sub_network: action.payload
});

const addNetworkSubNetworkReducer = (state: NetworkState, action: PayloadAction<Network>): NetworkState => ({
    ...state, network_sub_networks: [action.payload, ...state.network_sub_networks]
});

const updateNetworkSubNetworkReducer = (state: NetworkState, action: PayloadAction<Network>): NetworkState => ({
    ...state, network_sub_networks: state.network_sub_networks.map((sub_network) => sub_network.id === action.payload.id ? action.payload : sub_network)
});
const loadSubNetworksReducer = (state: NetworkState): NetworkState => ({
    ...state, network_sub_networks_loading: false
});
const updateNetworkSubNetworksReducer = (state: NetworkState, action: PayloadAction<Network[]>): NetworkState => ({
    ...state, network_sub_networks: action.payload, network_sub_networks_loading: false
});

const deleteNetworkSubNetworkReducer = (state: NetworkState, action: PayloadAction<number>): NetworkState => ({
    ...state, network_sub_networks: state.network_sub_networks.filter((sub_network) => sub_network.id !== action.payload)
});

const addNetworkListConfigurationPreferenceReducer = (state: NetworkState, action: PayloadAction<Preferences[]>): NetworkState => ({
    ...state, network_list_configuration: { ...state.network_list_configuration, preferences: [...state.network_list_configuration.preferences, ...action.payload] }
});

const setNetworkListConfigurationPreferenceReducer = (state: NetworkState, action: PayloadAction<Preferences[]>): NetworkState => ({
    ...state, network_list_configuration: { ...state.network_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: NetworkState, action: PayloadAction<Preferences[]>): NetworkState => {
    let main_preference = {};
    if(action.payload.find(value => value.module === NETWORK_MODULE)) {
        const { preferences, columns, order_by, order } = setNetworkPreference(action.payload, state.network_list_configuration.columns);
        main_preference = {
            network_list_configuration: { ...state.network_list_configuration, preferences, columns },
            network_list_criteria: { ...state.network_list_criteria, order, order_by },
        };
    }
    let margin_pref = {};
    if(action.payload.find(value => value.module === NETWORK_MARGIN_MODULE)) {
        const { margins_preferences, margins_columns, margins_order_by, margins_order } = setMarginPreference(action.payload, state.margins_configuration.columns);
        margin_pref = {
            margins_configuration: { ...state.margins_configuration, preferences: margins_preferences, columns: margins_columns },
            margins_criteria: { ...state.margins_criteria, order: margins_order, order_by: margins_order_by }
        };
    }
    return {
        ...state,
        ...main_preference,
        ...margin_pref,
        first_preference_loaded: true
    };
};

const toggleColumnReducer = (state: NetworkState, action: PayloadAction<number>): NetworkState => {
    const columns = cloneDeep([...state.network_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, network_list_configuration: { ...state.network_list_configuration, columns } };
};

const updateNetworkListReducer = (state: NetworkState, action: PayloadAction<Network[]>): NetworkState => ({
    ...state, network_list: action.payload
});
const updateNetworkListCriteriaReducer = (state: NetworkState, action: PayloadAction<NetworkListCriteria>): NetworkState => ({
    ...state, network_list_criteria: action.payload
});
const updateNetworkReducer = (state: NetworkState, action: PayloadAction<Network>): NetworkState => ({
    ...state, network: action.payload
});
const updateNetworkListLoadingReducer = (state: NetworkState, action: PayloadAction<boolean>): NetworkState => ({
    ...state, network_list_loading: action.payload
});
const updateNetworkListCountReducer = (state: NetworkState, action: PayloadAction<number>): NetworkState => ({
    ...state, network_list_count: action.payload
});

const setColumnsReducer = (state: NetworkState, action: PayloadAction<Column[]>): NetworkState => ({
    ...state, network_list_configuration: { ...state.network_list_configuration, columns: action.payload },
});

const setMarginColumnsReducer = (state: NetworkState, action: PayloadAction<Column[]>): NetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, columns: action.payload },
});

export const network = createSlice({
    name: 'network',
    initialState,
    reducers: {
        setSuspendedMargin: setSuspendedMarginReducer,
        addMarginsConfigurationPreference: addMarginsConfigurationPreferenceReducer,
        setMarginsConfigurationPreference: setMarginsConfigurationPreferenceReducer,
        toggleColumnMargins: toggleColumnMarginsReducer,
        setNetworkMargins: setNetworkMarginsReducer,
        updateMarginsCriteria: updateMarginsCriteriaReducer,
        updateMargin: updateMarginReducer,
        updateMarginsLoading: updateMarginsLoadingReducer,
        updateMarginsCount: updateMarginsCountReducer,
        addNetworkContact: addNetworkContactReducer,
        updateNetworkContact: updateNetworkContactReducer,
        updateNetworkContacts: updateNetworkContactsReducer,
        deleteNetworkContact: deleteNetworkContactReducer,
        setNetworkSubNetwork: setNetworkSubNetworkReducer,
        addNetworkSubNetwork: addNetworkSubNetworkReducer,
        updateNetworkSubNetwork: updateNetworkSubNetworkReducer,
        loadSubNetworks: loadSubNetworksReducer,
        updateNetworkSubNetworks: updateNetworkSubNetworksReducer,
        deleteNetworkSubNetwork: deleteNetworkSubNetworkReducer,
        addNetworkListConfigurationPreference: addNetworkListConfigurationPreferenceReducer,
        setNetworkListConfigurationPreference: setNetworkListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateNetworkList: updateNetworkListReducer,
        updateNetworkListCriteria: updateNetworkListCriteriaReducer,
        updateNetwork: updateNetworkReducer,
        updateNetworkListLoading: updateNetworkListLoadingReducer,
        updateNetworkListCount: updateNetworkListCountReducer,
        setColumns: setColumnsReducer,
        setMarginColumns: setMarginColumnsReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setSuspendedMargin,
    addMarginsConfigurationPreference,
    setMarginsConfigurationPreference,
    setColumns,
    toggleColumnMargins,
    setNetworkMargins,
    updateMarginsCriteria,
    updateMargin,
    updateMarginsLoading,
    updateMarginsCount,
    addNetworkContact,
    updateNetworkContact,
    updateNetworkContacts,
    deleteNetworkContact,
    setNetworkSubNetwork,
    addNetworkSubNetwork,
    updateNetworkSubNetwork,
    loadSubNetworks,
    updateNetworkSubNetworks,
    deleteNetworkSubNetwork,
    addNetworkListConfigurationPreference,
    setNetworkListConfigurationPreference,
    toggleColumn,
    updateNetworkList,
    updateNetworkListCriteria,
    updateNetwork,
    updateNetworkListLoading,
    updateNetworkListCount,
    setMarginColumns
} = network.actions;
